import { httpClient, publicHttpClient } from '../api/http-client';

export interface Presentacion {
  id: string;
  productoId: string;
  nombre: string;
  factor: number;
  precio: number;
  precioAntes?: number;
  precioCompra?: number;
  stock?: number;
  esPrincipal: boolean;
  activo: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Lote {
  id: string;
  productoId: string;
  presentacionId: string;
  numeroLote: string;
  cantidad: number;
  cantidadInicial?: number;
  costoUnitario?: number;
  fechaVencimiento?: string;
  fechaIngreso: string;
  proveedor?: string;
  numeroFactura?: string;
  proveedorId?: string;
}

export interface KardexLoteItem {
  loteId: string;
  numeroLote: string;
  productoId: string;
  productoNombre: string;
  presentacionNombre: string;
  cantidadInicial: number;
  cantidadActual: number;
  costoUnitario: number;
  totalVendido: number;
  totalBajas: number;
  totalVendidoProducto: number;
  totalVendidoPresentaciones: number;
  esperado: number;
  diferencia: number;
  fechaVencimiento: string | null;
  fechaIngreso: string;
  numeroFactura: string | null;
  proveedor: string | null;
}

export interface ProductoImagen {
  id: string;
  productoId: string;
  url: string;
  orden: number;
  creadoEn: string;
}

export interface Product {
  id: string;
  codigoBarras?: string;
  nombre: string;
  variante?: string;
  descripcion?: string;
  categoria: string;
  imagenes: ProductoImagen[];
  requiereReceta: boolean;
  activo: boolean;
  registroInvima?: string;
  laboratorio?: string;
  principioActivo?: string;
  ubicacion?: string;
  stockBase: number;
  presentaciones: Presentacion[];
  lotes: Lote[];
  creadoPorId?: string;
  creadoPor?: { id: string; nombreCompleto: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductSearchResult {
  id: string;
  codigoBarras?: string;
  nombre: string;
  variante?: string;
  categoria: string;
  imagenes: ProductoImagen[];
  requiereReceta: boolean;
  stockBase: number;
  ubicacion?: string;
  precio: number;
  stock: number;
  presentaciones: {
    id: string;
    nombre: string;
    factor: number;
    precio: number;
    precioAntes?: number;
    precioCompra?: number;
    stock: number;
  }[];
}

export interface PaginatedProductsResponse {
  data: Product[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface SearchProductsResponse {
  success: boolean;
  data: ProductSearchResult[];
  message?: string;
}

export interface ProductResponse {
  success: boolean;
  data: Product;
  message?: string;
}

export interface ProductByBarcodeResponse {
  success: boolean;
  data: ProductSearchResult;
  message?: string;
}

export interface ProductoInvima {
  id: string;
  expediente?: string;
  producto: string;
  principioActivo?: string;
  registroSanitario: string;
  estadoRegistro?: string;
  modalidad?: string;
  titular?: string;
  creadoEn: string;
}

export interface SearchInvimaResponse {
  success: boolean;
  data: ProductoInvima[];
  message?: string;
}

export class ProductsService {
  private static readonly BASE_PATH = '/products';

  static async search(query: string, limit: number = 10): Promise<SearchProductsResponse> {
    try {
      const response = await publicHttpClient.get<SearchProductsResponse>(
        `${this.BASE_PATH}/public/search?q=${encodeURIComponent(query)}&limit=${limit}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al buscar productos',
      };
    }
  }

  static async findByBarcode(barcode: string): Promise<ProductByBarcodeResponse> {
    try {
      const response = await publicHttpClient.get<ProductByBarcodeResponse>(
        `${this.BASE_PATH}/barcode/${encodeURIComponent(barcode)}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as ProductSearchResult,
        message: error.response?.data?.message || 'Producto no encontrado',
      };
    }
  }

  static async getPopular(limit: number = 8): Promise<SearchProductsResponse> {
    try {
      const response = await publicHttpClient.get<SearchProductsResponse>(
        `${this.BASE_PATH}/popular?limit=${limit}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al obtener productos populares',
      };
    }
  }

  static async getPublic(limit: number = 100, categoria?: string): Promise<SearchProductsResponse> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (categoria) params.append('categoria', categoria);

      const response = await publicHttpClient.get<SearchProductsResponse>(
        `${this.BASE_PATH}/public?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al cargar productos',
      };
    }
  }

  static async searchInvima(query: string, limit: number = 20): Promise<SearchInvimaResponse> {
    try {
      const response = await httpClient.get<SearchInvimaResponse>(
        `${this.BASE_PATH}/invima/search?q=${encodeURIComponent(query)}&limit=${limit}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al buscar en INVIMA',
      };
    }
  }

  static async list(
    page: number = 1,
    limit: number = 20,
    search?: string,
    categoriaId?: string,
    creadoPorId?: string,
    ubicacion?: string,
    activo?: boolean,
    stockStatus?: 'agotado' | 'bajo' | 'disponible'
  ): Promise<{ success: boolean; data: PaginatedProductsResponse; message?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (categoriaId) params.append('categoriaId', categoriaId);
      if (creadoPorId) params.append('creadoPorId', creadoPorId);
      if (ubicacion) params.append('ubicacion', ubicacion);
      if (activo !== undefined) params.append('activo', String(activo));
      if (stockStatus) params.append('stockStatus', stockStatus);

      const response = await httpClient.get<{ success: boolean; data: PaginatedProductsResponse }>(
        `${this.BASE_PATH}?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: { data: [], meta: { total: 0, page: 1, limit: 20, totalPages: 0, hasNextPage: false, hasPreviousPage: false } },
        message: error.response?.data?.message || 'Error al cargar productos',
      };
    }
  }

  static async getById(id: string): Promise<ProductResponse> {
    try {
      const response = await httpClient.get<ProductResponse>(`${this.BASE_PATH}/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Product,
        message: error.response?.data?.message || 'Error al cargar el producto',
      };
    }
  }

  static async create(data: Partial<Product> & { imagenesFiles?: File[] }): Promise<ProductResponse> {
    try {
      const formData = new FormData();
      formData.append('nombre', data.nombre || '');
      if (data.descripcion) formData.append('descripcion', data.descripcion);
      formData.append('categoria', data.categoria || '');
      formData.append('receta', String(data.requiereReceta ?? false));
      if (data.codigoBarras) formData.append('codigoBarras', data.codigoBarras);
      if (data.variante) formData.append('variante', data.variante);
      if (data.registroInvima) formData.append('registroInvima', data.registroInvima);
      if (data.laboratorio) formData.append('laboratorio', data.laboratorio);
      if (data.principioActivo) formData.append('principioActivo', data.principioActivo);
      if (data.ubicacion) formData.append('ubicacion', data.ubicacion);
      formData.append('stockBase', String(data.stockBase ?? 0));
      if (data.presentaciones) {
        formData.append('presentaciones', JSON.stringify(data.presentaciones));
      }
      if (data.imagenes && data.imagenes.length > 0) {
        formData.append('imagenesExistentes', JSON.stringify(data.imagenes));
      }
      if (data.imagenesFiles && data.imagenesFiles.length > 0) {
        data.imagenesFiles.forEach(file => formData.append('imagenes', file));
      }

      const response = await httpClient.post<ProductResponse>(this.BASE_PATH, formData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Product,
        message: error.response?.data?.message || 'Error al crear el producto',
      };
    }
  }

  static async update(id: string, data: Partial<Product> & { imagenesFiles?: File[] }): Promise<ProductResponse> {
    try {
      const formData = new FormData();
      if (data.nombre !== undefined) formData.append('nombre', data.nombre);
      if (data.descripcion !== undefined) formData.append('descripcion', data.descripcion);
      if (data.categoria !== undefined) formData.append('categoria', data.categoria);
      if (data.requiereReceta !== undefined) formData.append('receta', String(data.requiereReceta));
      if (data.codigoBarras !== undefined) formData.append('codigoBarras', data.codigoBarras);
      if (data.variante !== undefined) formData.append('variante', data.variante);
      if (data.registroInvima !== undefined) formData.append('registroInvima', data.registroInvima);
      if (data.laboratorio !== undefined) formData.append('laboratorio', data.laboratorio);
      if (data.principioActivo !== undefined) formData.append('principioActivo', data.principioActivo);
      if (data.ubicacion !== undefined) formData.append('ubicacion', data.ubicacion);
      if (data.stockBase !== undefined) formData.append('stockBase', String(data.stockBase));
      if (data.presentaciones) {
        formData.append('presentaciones', JSON.stringify(data.presentaciones));
      }
      if (data.imagenes && data.imagenes.length > 0) {
        formData.append('imagenesExistentes', JSON.stringify(data.imagenes));
      }
      if (data.imagenesFiles && data.imagenesFiles.length > 0) {
        data.imagenesFiles.forEach(file => formData.append('imagenes', file));
      }

      const response = await httpClient.patch<ProductResponse>(`${this.BASE_PATH}/${id}`, formData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Product,
        message: error.response?.data?.message || 'Error al actualizar el producto',
      };
    }
  }

  static async delete(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await httpClient.delete<{ success: boolean }>(`${this.BASE_PATH}/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar el producto',
      };
    }
  }

  // Presentaciones
  static async addPresentacion(productId: string, data: Omit<Presentacion, 'id' | 'productoId' | 'creadoEn' | 'actualizadoEn'>): Promise<{ success: boolean; data: Presentacion; message?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; data: Presentacion }>(`${this.BASE_PATH}/${productId}/presentaciones`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Presentacion,
        message: error.response?.data?.message || 'Error al agregar presentación',
      };
    }
  }

  static async updatePresentacion(presentacionId: string, data: Partial<Presentacion>): Promise<{ success: boolean; data: Presentacion; message?: string }> {
    try {
      const response = await httpClient.patch<{ success: boolean; data: Presentacion }>(`${this.BASE_PATH}/presentaciones/${presentacionId}`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Presentacion,
        message: error.response?.data?.message || 'Error al actualizar presentación',
      };
    }
  }

  static async deletePresentacion(presentacionId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await httpClient.delete<{ success: boolean }>(`${this.BASE_PATH}/presentaciones/${presentacionId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar presentación',
      };
    }
  }

  // Lotes
  static async addLote(productId: string, data: { presentacionId: string; numeroLote: string; cantidad: number; costoUnitario?: number; fechaVencimiento?: string; proveedor?: string; numeroFactura?: string; proveedorId?: string }): Promise<{ success: boolean; data: Lote; message?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; data: Lote }>(`${this.BASE_PATH}/${productId}/lotes`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Lote,
        message: error.response?.data?.message || 'Error al agregar lote',
      };
    }
  }

  static async getLotesByProduct(productId: string): Promise<{ success: boolean; data: Lote[]; message?: string }> {
    try {
      const response = await httpClient.get<{ success: boolean; data: Lote[] }>(`${this.BASE_PATH}/${productId}/lotes`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al cargar lotes',
      };
    }
  }

  static async getKardexLotes(productoId?: string, search?: string): Promise<{ success: boolean; data: KardexLoteItem[]; message?: string }> {
    try {
      const params = new URLSearchParams();
      if (productoId) params.append('productoId', productoId);
      if (search) params.append('search', search);
      const response = await httpClient.get<{ success: boolean; data: KardexLoteItem[] }>(`${this.BASE_PATH}/kardex-lotes?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al cargar kardex',
      };
    }
  }

  static async updateLote(loteId: string, data: Partial<{ numeroLote: string; cantidad: number; cantidadInicial?: number; costoUnitario?: number; fechaVencimiento?: string; proveedor?: string; numeroFactura?: string; proveedorId?: string; presentacionId?: string }>): Promise<{ success: boolean; data: Lote; message?: string }> {
    try {
      const response = await httpClient.patch<{ success: boolean; data: Lote }>(`${this.BASE_PATH}/lotes/${loteId}`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: {} as Lote,
        message: error.response?.data?.message || 'Error al actualizar lote',
      };
    }
  }

  static async deleteLote(loteId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await httpClient.delete<{ success: boolean }>(`${this.BASE_PATH}/lotes/${loteId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar lote',
      };
    }
  }

  static async downloadImageBackup(): Promise<Blob> {
    const response = await httpClient.get<Blob>(`${this.BASE_PATH}/backup-images`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Bajas
  static async darDeBaja(productId: string, data: {
    motivo: string;
    cantidad: number;
    observaciones?: string;
    presentacionId?: string;
    loteId?: string;
  }): Promise<{ success: boolean; data?: any; message?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; data: any }>(`${this.BASE_PATH}/${productId}/baja`, data);
      return response.data;
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Error al dar de baja el producto' };
    }
  }

  static async reactivar(productId: string): Promise<ProductResponse> {
    try {
      const response = await httpClient.patch<ProductResponse>(`${this.BASE_PATH}/${productId}/reactivar`);
      return response.data;
    } catch (error: any) {
      return { success: false, data: {} as Product, message: error.response?.data?.message || 'Error al reactivar el producto' };
    }
  }

  static async listarBajas(
    page: number = 1,
    limit: number = 20,
    search?: string,
    motivo?: string
  ): Promise<{ success: boolean; data: any[]; meta?: any; message?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (motivo) params.append('motivo', motivo);
      const response = await httpClient.get<{ success: boolean; data: any[]; meta: any }>(`${this.BASE_PATH}/bajas?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      return { success: false, data: [], message: error.response?.data?.message || 'Error al cargar bajas' };
    }
  }
}
